import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, withModifiers as _withModifiers, unref as _unref, createBlock as _createBlock, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "global-filters" }
const _hoisted_2 = { class: "expansion-panel-title" }
const _hoisted_3 = { class: "expansion-panel-text" }

import { computed, ref, watch, onMounted } from "vue";
import type { LayerSpecification, Map } from "maplibre-gl";

import { 
  PrimSource,
  RenewableSources,
  TraditionalSources,
} from "@/assets/power_plants";

import { POWER_PLANT_COLORS } from "@/composables/addPowerPlants";

import { setLayerVisibility } from "@/maplibre_controls";

interface Props {
  map: Map;
  layerId: string;
}

const minColumnWidthPx = 170;

type PlantCategory = typeof PLANT_CATEGORIES[number];


export default /*@__PURE__*/_defineComponent({
  __name: 'PowerPlantsFilterControl',
  props: {
    map: {},
    layerId: {}
  },
  setup(__props: any) {

const props = __props;

const openPanels = ref<number[]>([]);
const columns = ref(2);
const cssVars = computed(() => ({
  "--column-count": columns.value, 
}));

const PLANT_CATEGORIES = ["Renewables", "Fossil Fuels", "Other"] as const;
const selectedSources = ref<PrimSource[]>(Object.values(PrimSource));

const SOURCES_BY_CATEGORY: Record<PlantCategory, readonly PrimSource[]> = {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  Renewables: RenewableSources,
  "Fossil Fuels": TraditionalSources,
  // eslint-disable-next-line @typescript-eslint/naming-convention
  Other: Object.values(PrimSource).filter(source => 
    !((RenewableSources as unknown as PrimSource[]).includes(source) || 
      (TraditionalSources as unknown as PrimSource[]).includes(source))
  ),
};

const icons: Record<PrimSource, string> = {
  batteries: "mdi-battery-charging",
  biomass: "mdi-campfire",
  coal: "mdi-grill",
  geothermal: "fa-earth-americas",
  hydroelectric: "fa-droplet",
  "natural gas": "mdi-gas-burner",
  nuclear: "fa-atom",
  other: "mdi-circle",
  petroleum: "mdi-gas-station",
  "pumped storage": "mdi-water-pump",
  solar: "fa-solar-panel",
  wind: "mdi-wind-power",
};

function openPanel(index: number) {
  if (!openPanels.value.includes(index)) {
    openPanels.value.push(index);
  }
}

function openAllPanels() {
  openPanels.value = [...PLANT_CATEGORIES.keys()];
}

// function updateSelectedSources(source: PrimSource, value: boolean) {
//   console.log("updating");
//   const index = selectedSources.value.indexOf(source);
//   if (value && index === -1) {
//     selectedSources.value = [...selectedSources.value, source];
//   } else if (!value && index >= 0) {
//     selectedSources.value = selectedSources.value.slice(0, index).concat(selectedSources.value.slice(index + 1));
//   }
// }

function turnOnPowerPlants() {
  setLayerVisibility(props.map, "power-plants-layer", true);
}

function handleGlobalSelect(value: boolean) {
  selectedSources.value = value ? [...Object.values(PrimSource)] : [];
  turnOnPowerPlants();
}

function handleCategoryGlobalSelect(category: PlantCategory, value: boolean) {
  if (value) {
    // We could update the ref directly and use a deep watcher, but we don't need
    // to trigger UI updates on each push here - better to do it all at the end
    const sources = [...selectedSources.value];
    SOURCES_BY_CATEGORY[category].forEach(source => {
      if (!sources.includes(source)) {
        sources.push(source);
      }
    });
    selectedSources.value = sources;
  } else {
    selectedSources.value = selectedSources.value.filter(item => !SOURCES_BY_CATEGORY[category].includes(item));
  }
  turnOnPowerPlants();
}

let layers: LayerSpecification[] = [];

function onLayersChanged(newLayers: LayerSpecification[]) {
  layers = newLayers;
  applyPrimSourceFilter(selectedSources.value);
}

function updateColumnCount() {
  const container = document.querySelector("#power-plant-filter-controls");
  if (container) {
    columns.value = Math.max(Math.floor(container.clientWidth / minColumnWidthPx), 1);
  }
}


onMounted(() => {

  updateColumnCount();
  const container = document.querySelector("#power-plant-filter-controls");
  if (container) {
    const observer = new ResizeObserver(_entries => updateColumnCount());
    observer.observe(container);
  }

  props.map.on("styledata", () => {
    // check if layers changed
    // console.log('Style data event received');
    const newLayerIds = new Set((props.map.getStyle().layers || []).map(l => l.id));
    const oldLayerIds = new Set(layers.map(l => l.id));
    const hasNewLayers = newLayerIds.size !== oldLayerIds.size || [...newLayerIds].some(id => !oldLayerIds.has(id));
    if (hasNewLayers) {
      // console.log('Style data changed, updating layer list');
      onLayersChanged(props.map.getStyle().layers || []);
    }
    // check layer visibility changes
    const visibilityChanged = layers.some(oldLayer => {
      const newLayer = props.map.getStyle().layers?.find(l => l.id === oldLayer.id);
      return newLayer && oldLayer.layout?.visibility !== newLayer.layout?.visibility;
    });
    if (visibilityChanged) {
      console.log('Layer visibility changed, updating layer list');
      onLayersChanged(props.map.getStyle().layers || []);
    }
  });

  applyPrimSourceFilter(selectedSources.value);
});

watch(selectedSources, (sources: PrimSource[]) => {
  applyPrimSourceFilter(sources);
  turnOnPowerPlants();
});

function applyPrimSourceFilter(sources: PrimSource[]) {
  const layerIds = ["power-plants-layer", "power-plants-heatmap"];
  layerIds.forEach(id => {
    if (!props.map.getLayer(id)) { return; }
  
    if (sources.length === 0) {
      props.map.setFilter(id, false);
    } else if (sources.length === Object.values(PrimSource).length) {
      props.map.setFilter(id, null);
    } else {
      props.map.setFilter(id, ["in", ["get", "PrimSource"], ["literal", sources]]);
    }
  });
}

return (_ctx: any,_cache: any) => {
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_icon_checkbox = _resolveComponent("icon-checkbox")!
  const _component_v_expansion_panel = _resolveComponent("v-expansion-panel")!
  const _component_v_expansion_panels = _resolveComponent("v-expansion-panels")!

  return (_openBlock(), _createBlock(_component_v_expansion_panels, {
    id: "power-plant-filter-controls",
    multiple: "",
    modelValue: openPanels.value,
    "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((openPanels).value = $event)),
    style: _normalizeStyle(cssVars.value)
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _cache[8] || (_cache[8] = _createElementVNode("div", null, "Power Plant Filters", -1)),
        _createVNode(_component_v_btn, {
          onClick: _cache[0] || (_cache[0] = () => {
          openAllPanels();
          handleGlobalSelect(true);
        })
        }, {
          default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
            _createTextVNode("All", -1)
          ]))]),
          _: 1
        }),
        _createVNode(_component_v_btn, {
          onClick: _cache[1] || (_cache[1] = () => {
          openAllPanels();
          handleGlobalSelect(false);
        })
        }, {
          default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
            _createTextVNode("None", -1)
          ]))]),
          _: 1
        })
      ]),
      (_openBlock(), _createElementBlock(_Fragment, null, _renderList(PLANT_CATEGORIES, (category, index) => {
        return _createVNode(_component_v_expansion_panel, { key: index }, {
          title: _withCtx(() => [
            _createElementVNode("div", _hoisted_2, [
              _createElementVNode("span", null, _toDisplayString(category), 1),
              _createElementVNode("span", null, [
                _createVNode(_component_v_btn, {
                  onClick: [
                    () => {
                openPanel(index); 
                handleCategoryGlobalSelect(category, true);
              },
                    _cache[2] || (_cache[2] = _withModifiers(() => {}, ["stop"]))
                  ]
                }, {
                  default: _withCtx(() => [...(_cache[9] || (_cache[9] = [
                    _createTextVNode("All", -1)
                  ]))]),
                  _: 1
                }, 8, ["onClick"]),
                _createVNode(_component_v_btn, {
                  onClick: [
                    () => {
                openPanel(index); 
                handleCategoryGlobalSelect(category, false);
              },
                    _cache[3] || (_cache[3] = _withModifiers(() => {}, ["stop"]))
                  ]
                }, {
                  default: _withCtx(() => [...(_cache[10] || (_cache[10] = [
                    _createTextVNode("None", -1)
                  ]))]),
                  _: 1
                }, 8, ["onClick"])
              ])
            ])
          ]),
          text: _withCtx(() => [
            _createElementVNode("div", _hoisted_3, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(SOURCES_BY_CATEGORY[category], (source) => {
                return (_openBlock(), _createBlock(_component_icon_checkbox, {
                  key: source,
                  label: source,
                  value: source,
                  "on-icon": icons[source],
                  "off-icon": icons[source],
                  "on-color": _unref(POWER_PLANT_COLORS)[source],
                  "off-color": "gray",
                  modelValue: selectedSources.value,
                  "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((selectedSources).value = $event)),
                  density: "compact",
                  "hide-details": ""
                }, null, 8, ["label", "value", "on-icon", "off-icon", "on-color", "modelValue"]))
              }), 128))
            ])
          ]),
          _: 2
        }, 1024)
      }), 64))
    ]),
    _: 1
  }, 8, ["modelValue", "style"]))
}
}

})